import { useMemo } from 'react'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  LabelList,
  CartesianGrid,
  Tooltip,
} from 'recharts'

import { CustomizedLabel } from 'components/Shared/Charts/HorizontalBarChart/CustomizedLabel'
import { CustomTooltip } from 'components/Shared/Charts/HorizontalBarChart/CustomTooltip'
import {
  barSize,
  yAxisProps,
  containerStyles,
  BAR_AXIS_SPACE,
} from 'components/Shared/Charts/HorizontalBarChart/Constants'
import { isMobileView } from 'lib/GlobalConstants'
import styles from './HorizontalBarChart.module.scss'
import chartColors from 'assets/styles/scss/shared/_chart-colors.module.scss'
import variables from 'assets/styles/scss/shared/_variables.module.scss'

type Props = {
  yKey: string
  xKey: string
  data: { id: string; name: string; count: number }[]
  title: string
  height: number
}

const HorizontalBarChart = ({ yKey, xKey, title, data, height }: Props) => {
  let ctx: any
  const updatedData = data.sort((a: any, b: any) => b.count - a.count) // we need to show the highest value at the top

  const measureTextValue = (text: any) => {
    if (!ctx) {
      ctx = document?.createElement('canvas').getContext('2d') || {}
      ctx.font = "12px 'Roboto" //since safari dosent work properly with svg for rem, changed to px
      ctx.fontSynthesis = 'none'
    }

    return {
      width: ctx?.measureText?.(text)?.width,
      height: ctx?.measureText?.(text)?.height,
    }
  }
  /** memoised values are used in acccordance with the resize  */
  const maxTextWidthLeftAxis = useMemo(() => {
    return updatedData.reduce((acc: any, cur: any) => {
      const value = cur[xKey]
      const maxCharWidth =
        measureTextValue(value.toLocaleString().substring(0, 18))?.width + 9.47
      const width =
        value.length >= 18
          ? maxCharWidth
          : measureTextValue(value.toLocaleString())?.width

      if (width > acc) {
        return Math.round(width) >= 109 ? maxCharWidth : width
      }

      return acc
      // max length counted for roboto font for ""..." is 9.47 approx
    }, 0)
  }, [updatedData, xKey])

  const maxTextWidthRightAxis = useMemo(
    () =>
      updatedData.reduce((acc: any, cur: any) => {
        const value = cur[yKey]
        const width = measureTextValue(value.toLocaleString())?.width
        if (width > acc) {
          return width
        }
        return acc
      }, 0),
    [updatedData, yKey],
  )

  const horizontalChart = (
    <div
      data-testid="horizontal-barchart-container"
      style={containerStyles(height, data)}
      className={`${styles.scroll} horizontalChart-container`} //keeping styles since it causes to conflict with srollbar css
    >
      <ResponsiveContainer
        width={'100%'}
        height={barSize(updatedData, height).chartHeight}
        debounce={50}
        className={'bar-chart-container'}
      >
        <BarChart
          barGap={5}
          data={updatedData}
          layout="vertical"
          margin={{
            left: maxTextWidthLeftAxis - 40, // left side padding of the text
            right: maxTextWidthRightAxis + BAR_AXIS_SPACE, // right side padding of the number after bar is formed
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={chartColors.gray} />
          <Tooltip
            cursor={{ fill: chartColors.gray }}
            content={<CustomTooltip title={title} />}
            wrapperStyle={{
              ...(isMobileView ? { left: -85 } : {}),
            }}
          />
          <XAxis hide type="number" />

          <YAxis {...yAxisProps(xKey)} />
          <YAxis
            orientation="right"
            yAxisId={1}
            dataKey={yKey}
            type="category"
            axisLine={false}
            tickLine={false}
            tickFormatter={(value) => value.toLocaleString()}
            mirror
            tick={{
              transform: `translate(${
                maxTextWidthRightAxis + BAR_AXIS_SPACE
              }, 0)`,
              fontFamily: 'roboto',
              fontSize: '0.75rem',
              fill: variables['text-primary'],
            }}
            interval={0}
          />
          <Bar
            dataKey={yKey}
            minPointSize={2}
            barSize={barSize(updatedData, height).height}
          >
            <LabelList
              dataKey={xKey}
              position="left"
              content={<CustomizedLabel maxTextWidth={maxTextWidthLeftAxis} />}
            />
            {updatedData.map((d: any) => {
              return <Cell key={d[xKey]} fill={chartColors.iris} />
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )

  return updatedData.length ? horizontalChart : null
}

export default HorizontalBarChart
